/* tslint:disable */
/* eslint-disable */
/**
 * Internal Variable Definitions Administration API
 *  ## Introduction  Variable Definitions are centralized definitions of concrete variables which are typically present in multiple datasets. Variable Definitions support standardization of data and metadata and facilitate sharing and joining of data by clarifying when variables have an identical definition.  ## Maintenance of Variable Definitions This API allows for creation, maintenance and access of Variable Definitions.  ### Ownership Creation and maintenance of variables may only be performed by Statistics Norway employees representing a specific Dapla team, who are defined as the owners of a given Variable Definition. In order to create variables, the single team an owner currently represents must be specified in the bearer token. This is currently only supported from the Dapla Lab platform. All maintenance is to be performed by the owners, with no intervention from administrators.  ### Status All Variable Definitions have an associated status. The possible values for status are `DRAFT`, `PUBLISHED_INTERNAL` and `PUBLISHED_EXTERNAL`.  #### Draft When a Variable Definition is created it is assigned the status `DRAFT`. Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Mutable (it may be changed directly without need for versioning). - Not suitable to refer to from other systems.  This status may be changed to `PUBLISHED_INTERNAL` or `PUBLISHED_EXTERNAL` with a direct update.  #### Published Internal Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Immutable (all changes are versioned). - Suitable to refer to in internal systems for statistics production. - Not suitable to refer to for external use (for example in Statistikkbanken).  This status may be changed to `PUBLISHED_EXTERNAL` by creating a Patch version.  #### Published External Under this status the Variable Definition is:  - Visible to the general public. - Immutable (all changes are versioned). - Suitable to refer to from any system.  This status may not be changed as it would break immutability. If a Variable Definition is no longer relevant then its period of validity should be ended by specifying a `valid_until` date in a Patch version.  ### Immutability Variable Definitions are immutable. This means that any changes must be performed in a strict versioning system. Consumers can avoid being exposed to breaking changes by specifying a `date_of_validity` when they request a Variable Definition.  #### Patches Patches are for changes which do not affect the fundamental meaning of the Variable Definition.  #### Validity Periods Validity Periods are versions with a period defined by a `valid_from` date and optionally a `valid_until` date. If the fundamental meaning of a Variable Definition is to be changed, it should be done by creating a new Validity Period.  
 *
 * The version of the OpenAPI document: 0.1
 * Contact: metadata@ssb.no
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  ListVariableDefinitions200ResponseInner,
  Problem,
  SupportedLanguages,
} from '../models/index';
import {
    ListVariableDefinitions200ResponseInnerFromJSON,
    ListVariableDefinitions200ResponseInnerToJSON,
    ProblemFromJSON,
    ProblemToJSON,
    SupportedLanguagesFromJSON,
    SupportedLanguagesToJSON,
} from '../models/index';

export interface GetVariableDefinitionByIdRequest {
    variableDefinitionId: string;
    acceptLanguage?: SupportedLanguages;
    dateOfValidity?: Date | null;
    render?: boolean | null;
}

export interface ListVariableDefinitionsRequest {
    acceptLanguage?: SupportedLanguages;
    dateOfValidity?: Date | null;
    shortName?: string | null;
    render?: boolean | null;
}

/**
 * VariableDefinitionsApi - interface
 * 
 * @export
 * @interface VariableDefinitionsApiInterface
 */
export interface VariableDefinitionsApiInterface {
    /**
     * Get one variable definition.
     * @summary Get one variable definition.
     * @param {string} variableDefinitionId Unique identifier for the variable definition.
     * @param {SupportedLanguages} [acceptLanguage] Render the variable definition in the given language.
     * @param {Date} [dateOfValidity] List only variable definitions which are valid on this date.
     * @param {boolean} [render] Render the Variable Definition for presentation in a frontend
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof VariableDefinitionsApiInterface
     */
    getVariableDefinitionByIdRaw(requestParameters: GetVariableDefinitionByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListVariableDefinitions200ResponseInner>>;

    /**
     * Get one variable definition.
     * Get one variable definition.
     */
    getVariableDefinitionById(requestParameters: GetVariableDefinitionByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListVariableDefinitions200ResponseInner>;

    /**
     * List all variable definitions.
     * @summary List all variable definitions.
     * @param {SupportedLanguages} [acceptLanguage] Render the variable definition in the given language.
     * @param {Date} [dateOfValidity] List only variable definitions which are valid on this date.
     * @param {string} [shortName] List only the variable definition with the given short name.
     * @param {boolean} [render] Render the Variable Definition for presentation in a frontend
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof VariableDefinitionsApiInterface
     */
    listVariableDefinitionsRaw(requestParameters: ListVariableDefinitionsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<ListVariableDefinitions200ResponseInner>>>;

    /**
     * List all variable definitions.
     * List all variable definitions.
     */
    listVariableDefinitions(requestParameters: ListVariableDefinitionsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<ListVariableDefinitions200ResponseInner>>;

}

/**
 * 
 */
export class VariableDefinitionsApi extends runtime.BaseAPI implements VariableDefinitionsApiInterface {

    /**
     * Get one variable definition.
     * Get one variable definition.
     */
    async getVariableDefinitionByIdRaw(requestParameters: GetVariableDefinitionByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListVariableDefinitions200ResponseInner>> {
        if (requestParameters['variableDefinitionId'] == null) {
            throw new runtime.RequiredError(
                'variableDefinitionId',
                'Required parameter "variableDefinitionId" was null or undefined when calling getVariableDefinitionById().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['dateOfValidity'] != null) {
            queryParameters['date_of_validity'] = (requestParameters['dateOfValidity'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['render'] != null) {
            queryParameters['render'] = requestParameters['render'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (requestParameters['acceptLanguage'] != null) {
            headerParameters['Accept-Language'] = String(requestParameters['acceptLanguage']);
        }


        let urlPath = `/variable-definitions/{variable-definition-id}`;
        urlPath = urlPath.replace(`{${"variable-definition-id"}}`, encodeURIComponent(String(requestParameters['variableDefinitionId'])));

        const response = await this.request({
            path: urlPath,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListVariableDefinitions200ResponseInnerFromJSON(jsonValue));
    }

    /**
     * Get one variable definition.
     * Get one variable definition.
     */
    async getVariableDefinitionById(requestParameters: GetVariableDefinitionByIdRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListVariableDefinitions200ResponseInner> {
        const response = await this.getVariableDefinitionByIdRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List all variable definitions.
     * List all variable definitions.
     */
    async listVariableDefinitionsRaw(requestParameters: ListVariableDefinitionsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<ListVariableDefinitions200ResponseInner>>> {
        const queryParameters: any = {};

        if (requestParameters['dateOfValidity'] != null) {
            queryParameters['date_of_validity'] = (requestParameters['dateOfValidity'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['shortName'] != null) {
            queryParameters['short_name'] = requestParameters['shortName'];
        }

        if (requestParameters['render'] != null) {
            queryParameters['render'] = requestParameters['render'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (requestParameters['acceptLanguage'] != null) {
            headerParameters['Accept-Language'] = String(requestParameters['acceptLanguage']);
        }


        let urlPath = `/variable-definitions`;

        const response = await this.request({
            path: urlPath,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(ListVariableDefinitions200ResponseInnerFromJSON));
    }

    /**
     * List all variable definitions.
     * List all variable definitions.
     */
    async listVariableDefinitions(requestParameters: ListVariableDefinitionsRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<ListVariableDefinitions200ResponseInner>> {
        const response = await this.listVariableDefinitionsRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
